import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import Form from '@lib/components/ReactHookForm/FormContainer';
import { FormRef } from '@lib/components/ReactHookForm/types';
import useModalManagement from '@lib/hooks/useModalManagement';
import { Steps } from '@lib/enums/form';
import { pageStepsId } from '@lib/enums/selectors';
import DocumentImportFormFieldNames from '@lib/enums/fieldNames/documentImportFormFieldNames';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  DocumentationImportKindEnum,
  useEdmDocumentRenewalReminderTemplateQuery,
} from 'graphql-common';
import AssetsModalTableMultiselect from 'components/AssetsModalTableMultiselect/AssetsModalTableMultiselect';
import SiteAreasModalTableMultiselect from 'components/SiteAreasModalTableMultiselect/SiteAreasModalTableMultiselect';
import TasksTableMultiselect from 'components/TasksTableMultiselect/TasksTableMultiselect';
import FormTemplatesModalTableMultiselect from 'components/FormTemplatesModalTableMultiselect';
import useAiBudgetStatus from 'components/AiAssistant/hooks/useAiBudgetStatus';
import { getAiBudgetBlockedText } from 'components/AiAssistant/utils/getAiBudgetBlock';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import DocumentImportFormActions from './DocumentImportFormActions';
import getDocumentImportFormFields from './utils/getDocumentImportFormFields';
import useDocumentImportActions from './utils/useDocumentImportActions';

const SELECT_EQUIPMENT_MODAL = 'select-equipment';
const SELECT_AREAS_MODAL = 'select-areas';
const SELECT_TASKS_MODAL = 'select-tasks';
const SELECT_FORM_TEMPLATES_MODAL = 'select-form-templates';
const DOCUMENT_IMPORT_MODALS = [
  SELECT_EQUIPMENT_MODAL,
  SELECT_AREAS_MODAL,
  SELECT_TASKS_MODAL,
  SELECT_FORM_TEMPLATES_MODAL,
];

const DEFAULT_VALUES = {
  [DocumentImportFormFieldNames.SourceKind]: {
    value: DocumentationImportKindEnum.Files,
  },
  [DocumentImportFormFieldNames.Files]: [],
  [DocumentImportFormFieldNames.Archive]: [],
  [DocumentImportFormFieldNames.FtpUrl]: '',
  [DocumentImportFormFieldNames.FtpUsername]: '',
  [DocumentImportFormFieldNames.FtpPassword]: '',
  [DocumentImportFormFieldNames.AiEnhancements]: [],
};

export default function DocumentImport() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const formRef = useRef<FormRef>(undefined);
  const useModalManagementResult = useModalManagement(DOCUMENT_IMPORT_MODALS);

  const {
    getUserAccessByCompany,
    getUserAccessByComponent,
    isFullAdmin,
  }: ContextProps = useOutletContext();
  const isPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  // Renewal-reminder feature is locked when EDM is restricted at either
  // company plan or user-role level; skip its template query in both cases.
  const isDocumentationLocked =
    isPlanLocked ||
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead;
  const hasCategoriesAccess =
    getUserAccessByComponent(
      AccessComponentEnum.DocumentationCategoriesManagement,
    ) !== AccessLevelEnum.LimitedRead;
  const hasSitesAccess =
    getUserAccessByComponent(AccessComponentEnum.SitesManagement) !==
    AccessLevelEnum.LimitedRead;
  const hasAssetsAccess =
    getUserAccessByComponent(AccessComponentEnum.AssetsManagement) !==
    AccessLevelEnum.LimitedRead;
  const hasTasksAccess =
    getUserAccessByComponent(AccessComponentEnum.TasksManagement) !==
    AccessLevelEnum.LimitedRead;
  const hasUsersAccess =
    getUserAccessByComponent(AccessComponentEnum.UsersManagement) !==
    AccessLevelEnum.LimitedRead;
  // AI prefill options on the import form require the AiAssistance module on
  // the company plan (Write) AND the current user/team isn't individually
  // restricted from it.
  const isAiEnabled =
    getUserAccessByCompany(AccessComponentEnum.AiAssistance) ===
      AccessLevelEnum.Write &&
    getUserAccessByComponent(AccessComponentEnum.AiAssistance) ===
      AccessLevelEnum.Write;
  const { credits: aiCredits, blockedReason: aiBlockedReason } =
    useAiBudgetStatus(isAiEnabled);
  // `documentationImportCreate` refuses the whole import — files included —
  // when an AI enhancement is ticked without budget, so the control has to be
  // disabled before the user gets there (BE guide §8).
  const aiBlockedText = getAiBudgetBlockedText(t, aiBlockedReason);

  const {
    approverLoadOptions,
    areasLoadOptions,
    categoriesLoadOptions,
    equipmentLoadOptions,
    formTemplatesLoadOptions,
    graphQLErrors,
    loading,
    onCancel,
    onSubmit,
    sectorsLoadOptions,
    sitesLoadOptions,
    tasksLoadOptions,
  } = useDocumentImportActions();

  const { data: reminderTemplateData } =
    useEdmDocumentRenewalReminderTemplateQuery({
      fetchPolicy: 'cache-and-network',
      skip: isDocumentationLocked,
    });
  const renewalReminderTemplate = reminderTemplateData?.data ?? null;

  const openEquipmentModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_EQUIPMENT_MODAL);
  }, [useModalManagementResult]);

  const openAreasModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_AREAS_MODAL);
  }, [useModalManagementResult]);

  const openTasksModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_TASKS_MODAL);
  }, [useModalManagementResult]);

  const openFormTemplatesModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_FORM_TEMPLATES_MODAL);
  }, [useModalManagementResult]);

  const onCloseEquipmentModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_EQUIPMENT_MODAL);
  }, [useModalManagementResult]);

  const onCloseAreasModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_AREAS_MODAL);
  }, [useModalManagementResult]);

  const onCloseTasksModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_TASKS_MODAL);
  }, [useModalManagementResult]);

  const onCloseFormTemplatesModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_FORM_TEMPLATES_MODAL);
  }, [useModalManagementResult]);

  const onEquipmentSelect = useCallback(
    (values: string[], visibilityMap?: Record<string, boolean>) => {
      if (formRef.current) {
        formRef.current.setValue(
          DocumentFormFieldNames.Equipment,
          values.length
            ? values.map((id) => {
                const visibility = visibilityMap?.[id];
                return visibility === undefined
                  ? { value: id }
                  : { value: id, visibleViaQr: visibility };
              })
            : [],
          { shouldDirty: true, shouldTouch: true, shouldValidate: true },
        );
      }
    },
    [],
  );

  const onAreasSelect = useCallback(
    (values: string[], visibilityMap?: Record<string, boolean>) => {
      if (formRef.current) {
        formRef.current.setValue(
          DocumentFormFieldNames.Areas,
          values.length
            ? values.map((id) => {
                const visibility = visibilityMap?.[id];
                return visibility === undefined
                  ? { value: id }
                  : { value: id, visibleViaQr: visibility };
              })
            : [],
          { shouldDirty: true, shouldTouch: true, shouldValidate: true },
        );
      }
    },
    [],
  );

  const onTasksSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        DocumentFormFieldNames.Tasks,
        values.length ? values.map((id) => ({ value: id })) : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const onFormTemplatesSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        DocumentFormFieldNames.FormTemplates,
        values.length ? values.map((id) => ({ value: id })) : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  // The budget can run out while the form is open — between picking
  // enhancements and submitting. Disabling the select then would strand the
  // chips already in it: a disabled multi-select accepts no removals, so the
  // user could neither clear them nor submit (the mutation refuses the whole
  // import, files included, over flags they can't take back). Clearing the
  // selection as the block appears is what keeps the import submittable; the
  // reason under the field says why the options went away.
  const wasAiBlockedRef = useRef(!!aiBlockedText);
  useEffect(() => {
    const isBlocked = !!aiBlockedText;
    const justBlocked = isBlocked && !wasAiBlockedRef.current;
    wasAiBlockedRef.current = isBlocked;
    if (!justBlocked || !formRef.current) return;
    const selected = formRef.current.values?.[
      DocumentImportFormFieldNames.AiEnhancements
    ] as unknown[] | undefined;
    if (!selected?.length) return;
    formRef.current.setValue(DocumentImportFormFieldNames.AiEnhancements, [], {
      shouldDirty: true,
      shouldValidate: true,
    });
    // Only ever meaningful alongside code detection, which just went away.
    formRef.current.setValue(
      DocumentImportFormFieldNames.AiCodeInstructions,
      '',
      { shouldDirty: true, shouldValidate: true },
    );
  }, [aiBlockedText]);

  const formFields = useMemo(
    () =>
      getDocumentImportFormFields({
        t,
        approverLoadOptions,
        areasLoadOptions,
        categoriesLoadOptions,
        equipmentLoadOptions,
        formTemplatesLoadOptions,
        sectorsLoadOptions,
        sitesLoadOptions,
        tasksLoadOptions,
        renewalReminderTemplate,
        openEquipmentModal,
        openAreasModal,
        openFormTemplatesModal,
        openTasksModal,
        isFullAdmin,
        isPlanLocked,
        hasCategoriesAccess,
        hasSitesAccess,
        hasAssetsAccess,
        hasTasksAccess,
        hasUsersAccess,
        isAiEnabled,
        aiCredits,
        aiBlockedText,
      }),
    [
      t,
      approverLoadOptions,
      areasLoadOptions,
      categoriesLoadOptions,
      equipmentLoadOptions,
      formTemplatesLoadOptions,
      sectorsLoadOptions,
      sitesLoadOptions,
      tasksLoadOptions,
      renewalReminderTemplate,
      openEquipmentModal,
      openAreasModal,
      openFormTemplatesModal,
      openTasksModal,
      isFullAdmin,
      isPlanLocked,
      hasCategoriesAccess,
      hasSitesAccess,
      hasAssetsAccess,
      hasTasksAccess,
      hasUsersAccess,
      isAiEnabled,
      aiCredits,
      aiBlockedText,
    ],
  );

  const isEquipmentModalOpen = useModalManagementResult.isModalOpened(
    SELECT_EQUIPMENT_MODAL,
  );
  const isAreasModalOpen =
    useModalManagementResult.isModalOpened(SELECT_AREAS_MODAL);
  const isTasksModalOpen =
    useModalManagementResult.isModalOpened(SELECT_TASKS_MODAL);
  const isFormTemplatesModalOpen = useModalManagementResult.isModalOpened(
    SELECT_FORM_TEMPLATES_MODAL,
  );

  // Only include items the user has explicitly toggled in a prior modal
  // session — untouched rows must fall through to the modal's BE-baseline
  // (or, in this create-only flow, the column's default), not get
  // overridden by a default `true`.
  const buildVisibilityMapFromField = useCallback(
    (fieldName: string): Record<string, boolean> => {
      const list = formRef.current?.values?.[fieldName];
      if (!Array.isArray(list)) return {};
      return list.reduce<Record<string, boolean>>((acc, item) => {
        const id = String((item as { value?: string })?.value ?? '');
        if (!id) return acc;
        const value = (item as { visibleViaQr?: boolean })?.visibleViaQr;
        if (value === undefined) return acc;
        acc[id] = value;
        return acc;
      }, {});
    },
    [],
  );

  const equipmentInitialVisibilityMap = isEquipmentModalOpen
    ? buildVisibilityMapFromField(DocumentFormFieldNames.Equipment)
    : undefined;
  const areasInitialVisibilityMap = isAreasModalOpen
    ? buildVisibilityMapFromField(DocumentFormFieldNames.Areas)
    : undefined;

  const breadCrumbsData = useMemo(
    () => getBreadCrumbsDataByKey({ pathname, t }),
    [pathname, t],
  );

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      <div id={pageStepsId} />
      <DashboardContentMaxWidth>
        <PageBody withSteps>
          <WhiteBox verticalPaddings="large">
            <Form
              actionsComponent={DocumentImportFormActions}
              actionsComponentProps={{ onCancel }}
              defaultValues={DEFAULT_VALUES}
              fields={formFields}
              formId="documentation-import"
              formRef={formRef}
              loading={loading}
              onSubmit={onSubmit}
              graphQLErrors={graphQLErrors}
              graphQLErrorsReplacePathMap={{
                detectFields: DocumentImportFormFieldNames.AiEnhancements,
                'detectFields.name':
                  DocumentImportFormFieldNames.AiEnhancements,
                'detectFields.category':
                  DocumentImportFormFieldNames.AiEnhancements,
                'detectFields.description':
                  DocumentImportFormFieldNames.AiEnhancements,
                'detectFields.code':
                  DocumentImportFormFieldNames.AiEnhancements,
                'detectFields.tags':
                  DocumentImportFormFieldNames.AiEnhancements,
                'detectFields.codePrompt':
                  DocumentImportFormFieldNames.AiCodeInstructions,
              }}
              steps={[
                { title: t('documents-import-step-1') },
                { title: t('documents-import-step-2') },
              ]}
              stepsCount={Steps.step2}
            />
          </WhiteBox>
        </PageBody>
      </DashboardContentMaxWidth>
      <AssetsModalTableMultiselect
        isOpen={isEquipmentModalOpen}
        onClose={onCloseEquipmentModal}
        onSelect={onEquipmentSelect}
        formRef={formRef}
        fieldName={DocumentFormFieldNames.Equipment}
        // Free plan: each imported document is bound to a single asset, so
        // cap the picker the same way `DocumentForm` does.
        selectionLimit={isPlanLocked ? 1 : undefined}
        // "Visible via QR" is a premium EDM column — drop the prop on a
        // plan-locked company so the column doesn't appear in the picker.
        visibleViaQr={
          isPlanLocked
            ? undefined
            : { initialVisibilityMap: equipmentInitialVisibilityMap }
        }
      />
      <SiteAreasModalTableMultiselect
        isOpen={isAreasModalOpen}
        onClose={onCloseAreasModal}
        onSelect={onAreasSelect}
        formRef={formRef}
        fieldName={DocumentFormFieldNames.Areas}
        visibleViaQr={
          isPlanLocked
            ? undefined
            : { initialVisibilityMap: areasInitialVisibilityMap }
        }
      />
      <TasksTableMultiselect
        isOpen={isTasksModalOpen}
        onClose={onCloseTasksModal}
        onSelect={onTasksSelect}
        formRef={formRef}
        fieldName={DocumentFormFieldNames.Tasks}
      />
      <FormTemplatesModalTableMultiselect
        isOpen={isFormTemplatesModalOpen}
        onClose={onCloseFormTemplatesModal}
        onSelect={onFormTemplatesSelect}
        formRef={formRef}
        fieldName={DocumentFormFieldNames.FormTemplates}
      />
    </>
  );
}
